import * as React from 'react'
import { Button, ConfirmationDialog } from 'src/design-system'
import { Editor } from 'components/atoms/editor'
import { HtmlContent } from 'components/atoms/editor/html-content'
import { EditRemoveMenu } from 'components/checkin-shared'
import {
  CheckinQuestion,
  hasVisibleText,
  QuestionType,
  TYPE_LABELS,
} from './types'

const REMOVE_TITLE = 'Are you sure you want to remove this question?'
const REMOVE_BODY = 'Once the question is removed, you can not undo the action.'

// ── Question type badge ───────────────────────────────────────────────────────
// Figma "Common/Tiny • DemiBold": TT Talent, 14px / 16px line-height, +0.3px
// letter-spacing. Open-dialogue is Neutral-20/Neutral-80; Rating Scale is
// Beige-20/Beige-70. Those exact hues aren't in the project palette, so both
// are set via arbitrary values (Neutral tokens reused from the already-Figma-
// sourced #EFF1F8/#5E6580 used elsewhere in this module).
//
// Figma's weight is DemiBold (600), but TT Talent only ships Normal (400) and
// Medium (500) faces (app/packs/stylesheets/abstracts/_fonts.scss); 600 has no
// face and renders as faux-bold, so font-medium (the heaviest real face) is used.
const BADGE_STYLES: Record<QuestionType, { box: string; text: string }> = {
  open_dialogue: { box: 'bg-[#EFF1F8]', text: 'text-[#5E6580]' },
  rating_scale: { box: 'bg-[#F5F0EA]', text: 'text-[#886952]' },
}

const QuestionTypeBadge = ({ type }: { type: QuestionType }) => {
  const style = BADGE_STYLES[type]

  return (
    <div
      className={`px-2 py-1 rounded-sm inline-flex justify-center items-center overflow-hidden ${style.box}`}
    >
      <span
        className={`text-center font-talent text-sm font-medium leading-4 tracking-[0.3px] ${style.text}`}
      >
        {TYPE_LABELS[type]}
      </span>
    </div>
  )
}

// ── Question row ──────────────────────────────────────────────────────────────

export function QuestionRow({
  question,
  onUpdate,
  onRemove,
}: {
  question: CheckinQuestion
  onUpdate: (id: number, body: string) => Promise<void>
  onRemove: (id: number) => void | Promise<void>
}) {
  const [editing, setEditing] = React.useState(false)
  const [confirmingRemove, setConfirmingRemove] = React.useState(false)
  const [body, setBody] = React.useState(question.body)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  // Radix's kebab menu and confirm dialog lock `document.body` (pointer-events:
  // none + data-scroll-locked) while open and restore it on close. Confirming a
  // removal deletes the question, which unmounts this row — and the still-closing
  // dialog with it — before Radix runs that restore, leaving the whole page
  // unclickable until a hard refresh. Track a confirmed removal and release the
  // lock on unmount (same workaround as checkin-summary-page/header).
  const removedRef = React.useRef(false)
  React.useEffect(
    () => () => {
      if (!removedRef.current) return
      document.body.style.pointerEvents = ''
      document.body.removeAttribute('data-scroll-locked')
    },
    [],
  )

  const startEdit = () => {
    setBody(question.body)
    setError(null)
    setEditing(true)
  }

  const cancelEdit = () => {
    setEditing(false)
    setError(null)
    setBody(question.body)
  }

  const handleUpdate = async () => {
    if (!hasVisibleText(body)) {
      setError('Question text is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      await onUpdate(question.id, body.trim())
      setEditing(false)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200 flex flex-col gap-3">
        <div className="rounded-sm bg-gray-100 p-2">
          <Editor
            toolbar="static"
            initialContent={question.body}
            rows={3}
            onChange={(html) => setBody(html === '<p></p>' ? '' : html)}
          />
        </div>
        {error && (
          <p className="m-0 font-talent text-red-500 text-xs">{error}</p>
        )}
        <div className="flex justify-end items-center gap-2">
          <button
            type="button"
            onClick={cancelEdit}
            className="px-3.5 py-2 rounded-sm font-talent text-base font-medium text-gray-500 hover:text-gray-700 leading-5"
          >
            Cancel
          </button>
          <Button
            onClick={handleUpdate}
            disabled={saving}
            className="!bg-green-600 hover:!bg-green-700 !border-green-600 leading-5 font-medium text-base"
          >
            {saving ? 'Updating…' : 'Update'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 bg-white rounded-lg border border-gray-200 flex items-center gap-6">
      <HtmlContent className="min-w-0 flex-1 font-talent text-base font-normal text-indigo-950 leading-5 [&_p]:m-0 [&_p]:break-words">
        {question.body}
      </HtmlContent>

      <QuestionTypeBadge type={question.questionType} />

      <EditRemoveMenu
        triggerLabel="Question options"
        onEdit={startEdit}
        onRemove={() => setConfirmingRemove(true)}
      />

      {/* Shared design-system confirmation dialog; danger variant => red Remove
          button, matching Figma. */}
      <ConfirmationDialog.Root
        open={confirmingRemove}
        onOpenChange={setConfirmingRemove}
        title={REMOVE_TITLE}
        body={REMOVE_BODY}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        confirmColourVariant="danger"
        onConfirm={() => {
          removedRef.current = true
          setConfirmingRemove(false)
          return onRemove(question.id)
        }}
        onCancel={() => setConfirmingRemove(false)}
      />
    </div>
  )
}
